import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, AlertCircle, Users, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import { supabase } from '../../../lib/supabaseClient';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const STATUS_STYLES = {
  queued: 'bg-slate-100 text-slate-600 border-slate-200',
  running: 'bg-blue-100 text-blue-700 border-blue-200',
  done: 'bg-green-100 text-green-700 border-green-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-100',
};

/**
 * LinkedInScoutPanel - triggers and lists read-only LinkedIn scouting runs
 * for an RFQ. Nothing here posts, connects, or messages anyone - a separate
 * standalone process (agent-runner/) on the recruiter's own machine does the
 * actual searching/reading, and this just queues jobs for it and shows what
 * it found.
 *
 * Props:
 * - rfqId: UUID of the RFQ
 */
const LinkedInScoutPanel = ({ rfqId }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${API_URL}/linkedin-scout/jobs?rfqId=${rfqId}`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` },
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to load scouting jobs');
      }

      setJobs(result.jobs);
    } catch (err) {
      console.error('Error fetching LinkedIn scout jobs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [rfqId]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleSearch = async () => {
    try {
      setSearching(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${API_URL}/linkedin-scout/jobs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ rfqId }),
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to queue a scouting run');
      }

      await fetchJobs();
    } catch (err) {
      console.error('Error queuing LinkedIn scout job:', err);
      setError(err.message);
    } finally {
      setSearching(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Search className="h-5 w-5 mr-2 text-indigo-600" />
          LinkedIn Scouting
        </CardTitle>
        <CardDescription>
          Read-only. Queues a search on a separate runner on your own machine, which reads public
          profiles and reports back a ranked lead list - nothing is ever contacted automatically.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0 space-y-4">
        {error && (
          <Alert className="bg-red-50 border-red-200">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">{error}</AlertDescription>
          </Alert>
        )}

        <Button onClick={handleSearch} disabled={searching}>
          <Search className={`h-4 w-4 mr-2 ${searching ? 'animate-pulse' : ''}`} />
          {searching ? 'Queuing...' : 'Search LinkedIn for candidates'}
        </Button>

        {loading ? (
          <div className="flex items-center justify-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
          </div>
        ) : jobs.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No scouting runs yet for this RFQ. This needs a runner (agent-runner/) polling on your
            machine to actually pick a queued run up - see agent-runner/README.md.
          </p>
        ) : (
          <div className="space-y-2">
            {jobs.map((job) => (
              <div key={job.id} className="flex items-center justify-between border rounded-md px-3 py-2">
                <div className="flex items-center gap-3 min-w-0">
                  <Badge variant="outline" className={STATUS_STYLES[job.status] || ''}>
                    {job.status}
                  </Badge>
                  <span className="text-sm text-muted-foreground truncate">
                    {new Date(job.created_at).toLocaleString()}
                  </span>
                  {job.status === 'done' && (
                    <span className="text-sm flex items-center gap-1 text-muted-foreground">
                      <Users className="h-3.5 w-3.5" /> {job.lead_count} leads
                    </span>
                  )}
                  {job.status === 'failed' && job.error_message && (
                    <span className="text-sm text-red-600 truncate">{job.error_message}</span>
                  )}
                </div>
                {job.status === 'done' && job.lead_count > 0 && (
                  <Link to={`/recruiter/linkedin-leads/${job.id}`}>
                    <Button variant="outline" size="sm">
                      View leads <ExternalLink className="h-3.5 w-3.5 ml-1" />
                    </Button>
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LinkedInScoutPanel;
